import React, { useState } from 'react';
import { Box } from '@mui/material';
import { EnhancedLibraryRecord } from '../types/libraryTypes';
import { LibraryTableHeader } from './LibraryTableHeader';
import { LibraryRow } from './LibraryRow';
import { LibraryPagination } from './LibraryPagination';
import { LibraryLoadingState } from './LibraryLoadingState';
import { LibraryErrorState } from './LibraryErrorState';
import { LibraryEmptyState } from './LibraryEmptyState';

interface LibraryTableProps {
  data?: {
    records: EnhancedLibraryRecord[];
    totalCount: number;
    hasNextPage: boolean;
  };
  isLoading: boolean;
  isLoadingUserMetadata: boolean;
  error: any;
  searchQuery: string;
  pageNumber: number;
  setPageNumber: (page: number) => void;
  refetch: () => void;
}

export const LibraryTable: React.FC<LibraryTableProps> = ({ 
  data, 
  isLoading, 
  isLoadingUserMetadata,
  error, 
  searchQuery, 
  pageNumber, 
  setPageNumber, 
  refetch 
}) => {
  const libraryData = data?.records || [];
  
  const [expandedStates, setExpandedStates] = useState<Record<string, boolean>>({});

  const handleExpandChange = (itemId: string, expanded: boolean) => {
    setExpandedStates((prev) => ({
      ...prev,
      [itemId]: expanded
    }));
  };

  // Loading state
  if (isLoading || (libraryData.length > 0 && isLoadingUserMetadata)) {
    return <LibraryLoadingState />;
  }

  // Error state
  if (error) {
    return <LibraryErrorState onRetry={refetch} />;
  }

  // Empty state - replaces entire table when no data exists
  if (libraryData.length === 0) {
    return <LibraryEmptyState searchQuery={searchQuery} />;
  }

  return (
    <Box sx={{ WebkitFontSmoothing: 'antialiased' }}>
      <LibraryTableHeader />
      
      {/* Table Content */}
      <Box>
        {libraryData.map((item, index) => {
          const itemId = item.retailerSku || `item-${index}`;
          const previousItem = index > 0 ? libraryData[index - 1] : null;
          const previousItemId = previousItem ? (previousItem.retailerSku || `item-${index - 1}`) : '';
          const previousItemExpanded = index > 0 ? expandedStates[previousItemId] : false;

          return (
            <LibraryRow
              key={itemId}
              item={item}
              isFirst={index === 0}
              isLast={index === libraryData.length - 1}
              previousItemExpanded={previousItemExpanded}
              onExpandChange={handleExpandChange}
            />
          );
        })}
      </Box>

      {/* Pagination - hide when only one page */}
      {(pageNumber > 1 || data?.hasNextPage) && (
        <LibraryPagination
          pageNumber={pageNumber}
          setPageNumber={setPageNumber}
          hasNextPage={data?.hasNextPage || false}
        />
      )}
    </Box>
  );
};
